import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
    AdditionalInfoCreateData, AdditionalInfoTemplateRequest, AdditionalInfoTemplateResponse, AdditionalInfoTemplateTableResponse,
    AddPhaseNodeRequest, CreatePlanningTemplateRequest, ParsePlanningCsvRequest, PhaseNodeResponse, PlanningTemplateDTO, PlanningTemplateResponse,
    UpdatePhaseNodeRequest, UpdatePlanningTemplateRequest, UpdatePlanningTreeRequest, initialValue,
} from "./template-intefaces";
import { ErrorResponse, MainResponse, RejectedValue } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import { showNotification } from "shared/slices/statusNotificationsSlice";
import { NotificationTypes } from "shared/interfaces/status-notifications-interfaces";
import { API_BASE_URL as baseUri } from "config/env";

//#region Planning Templates

export const GetActivePlanningTemplates = createAsyncThunk<PlanningTemplateDTO[], void, { rejectValue: RejectedValue }>(
    'Slice/TemplateSlice/GetActivePlanningTemplates',
    async (_, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/planning-templates/get-all?isActive=true', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as PlanningTemplateDTO[];
            // dispatch(showNotification({ message: "Planning templates loaded successfully", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetInactivePlanningTemplates = createAsyncThunk<PlanningTemplateDTO[], void, { rejectValue: RejectedValue }>(
    'Slice/TemplateSlice/GetInactivePlanningTemplates',
    async (_, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/planning-templates/get-all?isActive=false', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as PlanningTemplateDTO[];
            // dispatch(showNotification({ message: "Inactive planning templates loaded successfully", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetPlanningTemplateById = createAsyncThunk<PlanningTemplateResponse, string, { rejectValue: RejectedValue }>(
    'Slice/TemplateSlice/GetPlanningTemplateById',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/planning-templates/get?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const getModel = await response.json() as PlanningTemplateResponse;
            // dispatch(showNotification({ message: "Planning template loaded successfully", type: NotificationTypes.Success }));
            return getModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreatePlanningTemplate = createAsyncThunk<MainResponse, CreatePlanningTemplateRequest, { rejectValue: RejectedValue }>(
    'Slice/TemplateSlice/CreatePlanningTemplate',
    async (request: CreatePlanningTemplateRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/planning-templates/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request),
        });
        if (response.status === 200) {
            const createResponse = await response.json() as MainResponse;
            dispatch(GetActivePlanningTemplates());
            dispatch(showNotification({ message: createResponse.message || "Planning template successfully created.", type: NotificationTypes.Success }));
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdatePlanningTemplate = createAsyncThunk<MainResponse, UpdatePlanningTemplateRequest, { rejectValue: RejectedValue }>(
    'Slice/TemplateSlice/UpdatePlanningTemplate',
    async (request: UpdatePlanningTemplateRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/planning-templates/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request),
        });
        if (response.status === 200) {
            const createResponse = await response.json() as MainResponse;
            dispatch(GetActivePlanningTemplates());
            dispatch(showNotification({ message: createResponse.message || "Planning template successfully updated.", type: NotificationTypes.Success }));
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivatePlanningTemplate = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/TemplateSlice/DeactivatePlanningTemplate',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/planning-templates/deactivate?id=' + id, {
            method: "PATCH",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const deactivateResponse = await response.json() as MainResponse;
            dispatch(GetActivePlanningTemplates());
            dispatch(GetInactivePlanningTemplates());
            dispatch(showNotification({ message: deactivateResponse.message || "Planning template successfully deactivated.", type: NotificationTypes.Success }));
            return deactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ReactivatePlanningTemplate = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/TemplateSlice/ReactivatePlanningTemplate',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/planning-templates/reactivate?id=' + id, {
            method: "PATCH",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const reactivateResponse = await response.json() as MainResponse;
            dispatch(GetActivePlanningTemplates());
            dispatch(GetInactivePlanningTemplates());
            dispatch(showNotification({ message: reactivateResponse.message || "Planning template successfully reactivated.", type: NotificationTypes.Success }));
            return reactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeletePlanningTemplate = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/TemplateSlice/DeletePlanningTemplate',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/planning-templates/delete?id=' + id, {
            method: "DELETE",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const deleteResponse = await response.json() as MainResponse;
            dispatch(GetInactivePlanningTemplates());
            dispatch(showNotification({ message: deleteResponse.message || "Planning template successfully deleted.", type: NotificationTypes.Success }));
            return deleteResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdatePlanningTree = createAsyncThunk<PlanningTemplateResponse, UpdatePlanningTreeRequest, { rejectValue: RejectedValue }>(
    'Slice/TemplateSlice/UpdatePlanningTree',
    async (request: UpdatePlanningTreeRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/planning-templates/update-tree', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as PlanningTemplateResponse;
            dispatch(showNotification({ message: "Planning tree successfully updated.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ParsePlanningCsv = createAsyncThunk<PhaseNodeResponse[], ParsePlanningCsvRequest, { rejectValue: RejectedValue }>(
    'Slice/TemplateSlice/ParsePlanningCsv',
    async (request: ParsePlanningCsvRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/planning-templates/parse-csv', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request),
        });
        if (response.status === 200) {
            return await response.json() as PhaseNodeResponse[];
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

//#endregion

//#region Phase Nodes

export const CreatePhaseNode = createAsyncThunk<PhaseNodeResponse, AddPhaseNodeRequest, { rejectValue: RejectedValue }>(
    'Slice/TemplateSlice/CreatePhaseNode',
    async (request: AddPhaseNodeRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/phase-nodes/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request),
        });
        if (response.status === 200) {
            const createResponse = await response.json() as PhaseNodeResponse;
            dispatch(showNotification({ message: "Phase successfully created.", type: NotificationTypes.Success }));
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdatePhaseNode = createAsyncThunk<PhaseNodeResponse, UpdatePhaseNodeRequest, { rejectValue: RejectedValue }>(
    'Slice/TemplateSlice/UpdatePhaseNode',
    async (request: UpdatePhaseNodeRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/phase-nodes/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request),
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as PhaseNodeResponse;
            dispatch(showNotification({ message: "Phase successfully updated.", type: NotificationTypes.Success }));
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeletePhaseNode = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/TemplateSlice/DeletePhaseNode',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/phase-nodes/delete?id=' + id, {
            method: "DELETE",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const deleteResponse = await response.json() as MainResponse;
            dispatch(showNotification({ message: deleteResponse.message || "Phase successfully deleted.", type: NotificationTypes.Success }));
            return deleteResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

/**
 * Archives a phase node: it stays in the template and on existing trackings, but drops out of the
 * phase selection along with everything below it. Allowed however many trackings reference it —
 * unlike delete.
 */
export const ArchivePhaseNode = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/TemplateSlice/ArchivePhaseNode',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/phase-nodes/archive?id=' + id, {
            method: "PATCH",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const archiveResponse = await response.json() as MainResponse;
            dispatch(showNotification({ message: archiveResponse.message || "Phase successfully archived.", type: NotificationTypes.Success }));
            return archiveResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

/**
 * Clears a phase node's own archived flag. Rejected by the backend when a phase above it is still
 * archived — that one has to be unarchived first.
 */
export const UnarchivePhaseNode = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/TemplateSlice/UnarchivePhaseNode',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/phase-nodes/unarchive?id=' + id, {
            method: "PATCH",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const unarchiveResponse = await response.json() as MainResponse;
            dispatch(showNotification({ message: unarchiveResponse.message || "Phase successfully unarchived.", type: NotificationTypes.Success }));
            return unarchiveResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

//#endregion

//#region Additional Info Templates

export const GetAllAdditionalInfoTemplates = createAsyncThunk<AdditionalInfoTemplateTableResponse[], void, { rejectValue: RejectedValue }>(
    'Slice/TemplateSlice/GetAllAdditionalInfoTemplates',
    async (_, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/additionalinfotemplates/getall', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as AdditionalInfoTemplateTableResponse[];
            // dispatch(showNotification({ message: "Additional info templates loaded successfully", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetAllInactiveAdditionalInfoTemplates = createAsyncThunk<AdditionalInfoTemplateTableResponse[], void, { rejectValue: RejectedValue }>(
    'Slice/TemplateSlice/GetAllInactiveAdditionalInfoTemplates',
    async (_, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/additionalinfotemplates/getall-inactive', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as AdditionalInfoTemplateTableResponse[];
            // dispatch(showNotification({ message: "Inactive additional info templates loaded successfully", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetAdditionalInfoTemplate = createAsyncThunk<AdditionalInfoTemplateResponse, string, { rejectValue: RejectedValue }>(
    'Slice/TemplateSlice/GetAdditionalInfoTemplate',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/additionalinfotemplates/get?templateId=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getModel = await response.json() as AdditionalInfoTemplateResponse;
            // dispatch(showNotification({ message: "Additional information template loaded successfully", type: NotificationTypes.Success }));
            return getModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetAdditionalInfoCreateData = createAsyncThunk<AdditionalInfoCreateData, void, { rejectValue: RejectedValue }>(
    'Slice/TemplateSlice/GetAdditionalInfoCreateData',
    async (_, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/additionalinfotemplates/get-create-data', {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getModel = await response.json() as AdditionalInfoCreateData;
            // dispatch(showNotification({ message: "Additional info create data loaded successfully", type: NotificationTypes.Success }));
            return getModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateAdditionalInfoTemplate = createAsyncThunk<string, AdditionalInfoTemplateRequest, { rejectValue: RejectedValue }>(
    'Slice/TemplateSlice/CreateAdditionalInfoTemplate',
    async (template: AdditionalInfoTemplateRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/additionalinfotemplates/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(template)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            dispatch(GetAllAdditionalInfoTemplates());
            dispatch(showNotification({ message: "Additional information template successfully created.", type: NotificationTypes.Success }));
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateAdditionalInfoTemplate = createAsyncThunk<string, AdditionalInfoTemplateRequest, { rejectValue: RejectedValue }>(
    'Slice/TemplateSlice/UpdateAdditionalInfoTemplate',
    async (template: AdditionalInfoTemplateRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/additionalinfotemplates/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(template)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            dispatch(GetAllAdditionalInfoTemplates());
            dispatch(showNotification({ message: "Additional information template successfully updated.", type: NotificationTypes.Success }));
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateAdditionalInfoTemplate = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/TemplateSlice/DeactivateAdditionalInfoTemplate',
    async (templateId: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/additionalinfotemplates/deactivate?templateId=' + templateId, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            dispatch(GetAllAdditionalInfoTemplates());
            dispatch(GetAllInactiveAdditionalInfoTemplates());
            dispatch(showNotification({ message: createResponse || "Additional information template successfully deactivated.", type: NotificationTypes.Success }));
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ReactivateAdditionalInfoTemplate = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/TemplateSlice/ReactivateAdditionalInfoTemplate',
    async (templateId: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/additionalinfotemplates/reactivate?templateId=' + templateId, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            dispatch(GetAllAdditionalInfoTemplates());
            dispatch(GetAllInactiveAdditionalInfoTemplates());
            dispatch(showNotification({ message: createResponse || "Additional information template successfully reactivated.", type: NotificationTypes.Success }));
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeleteAdditionalInfoTemplate = createAsyncThunk<string, string, { rejectValue: RejectedValue }>(
    'Slice/TemplateSlice/DeleteAdditionalInfoTemplate',
    async (templateId: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/additionalinfotemplates/delete?templateId=' + templateId, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const createResponse = await response.json() as string;
            dispatch(GetAllInactiveAdditionalInfoTemplates());
            dispatch(showNotification({ message: createResponse || "Additional information template successfully deleted.", type: NotificationTypes.Success }));
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

//#endregion

export const TemplateSlice = createSlice({
    name: 'TemplateSlice',
    initialState: initialValue,
    reducers: {
        resetTemplateSlice: (state) => {
            state.planningTemplates = {
                listOfActivePlanningTemplates: [],
                listOfInactivePlanningTemplates: [],
                template: null,
            };
            state.additionalFieldsTemplates = {
                listOfActiveAdditionalFiledsTemplates: [],
                listOfInactiveAdditionalFiledsTemplates: [],
                createData: {
                    dataTypes: [],
                    templateTypes: []
                },
                template: null,
            };
        },
        resetPlanningTemplate: (state) => {
            state.planningTemplates.template = null;
        },
        resetAdditionalInfoTemplate: (state) => {
            state.additionalFieldsTemplates.template = null;
        },
    },
    extraReducers(builder) {
        //#region Planning Templates

        // Get Active Planning Templates
        builder
            .addCase(GetActivePlanningTemplates.fulfilled, (state, action: PayloadAction<PlanningTemplateDTO[]>) => {
                state.planningTemplates.listOfActivePlanningTemplates = action.payload;
            })

        // Get Inactive Planning Templates
        builder
            .addCase(GetInactivePlanningTemplates.fulfilled, (state, action: PayloadAction<PlanningTemplateDTO[]>) => {
                state.planningTemplates.listOfInactivePlanningTemplates = action.payload;
            })

        // Get Planning Template By Id
        builder
            .addCase(GetPlanningTemplateById.fulfilled, (state, action: PayloadAction<PlanningTemplateResponse>) => {
                state.planningTemplates.template = action.payload;
            })

        //#endregion

        //#region Additional Info Templates

        // Get Active Additional Info Templates
        builder
            .addCase(GetAllAdditionalInfoTemplates.fulfilled, (state, action: PayloadAction<AdditionalInfoTemplateTableResponse[]>) => {
                state.additionalFieldsTemplates.listOfActiveAdditionalFiledsTemplates = action.payload;
            })

        // Get Inactive Additional Info Templates
        builder
            .addCase(GetAllInactiveAdditionalInfoTemplates.fulfilled, (state, action: PayloadAction<AdditionalInfoTemplateTableResponse[]>) => {
                state.additionalFieldsTemplates.listOfInactiveAdditionalFiledsTemplates = action.payload;
            })

        // Get Additional Information Template
        builder
            .addCase(GetAdditionalInfoTemplate.fulfilled, (state, action: PayloadAction<AdditionalInfoTemplateResponse>) => {
                state.additionalFieldsTemplates.template = action.payload;
            })

        // Get Additional Info CreateData
        builder
            .addCase(GetAdditionalInfoCreateData.fulfilled, (state, action: PayloadAction<AdditionalInfoCreateData>) => {
                state.additionalFieldsTemplates.createData = action.payload;
            })

        //#endregion
    }
});

export const { resetPlanningTemplate, resetAdditionalInfoTemplate, resetTemplateSlice } = TemplateSlice.actions;
export default TemplateSlice.reducer;